import { useCallback, useEffect, useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { LayoutButton, toolbarButtonClass } from './LayoutButton'
import { ViewTypeSwitcher } from './ViewTypeSwitcher'
import type { ReactNode } from 'react'
import type { FilterFieldKey } from '@/lib/types/filter'
import type { ViewType } from '@/store/ProjectViewPreferencesStore'
import { FilterTriggerButton } from '@/components/features/filters'
import { Button } from '@/components/ui/button'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { cn } from '@/lib/utils'
import { useQuickAddStore } from '@/store/QuickAddStore'

interface ProjectViewControlsProps {
  projectId: string
  scope?: 'project' | 'space'
  pinnedFields?: Array<FilterFieldKey>
  hiddenFields?: Array<FilterFieldKey>
  /** Layout-specific Display options (calendar, timeline), owned by those views. */
  layoutSection?: Partial<Record<ViewType, ReactNode>>
}

/**
 * The page top bar's controls (design 26c): one layout switcher, then
 * Filter, Display and the primary action. Layout-specific tools (the
 * timeline's zoom, the calendar's month nav) live in the view's own bar.
 */
export function ProjectViewControls({
  projectId,
  scope = 'project',
  pinnedFields,
  hiddenFields,
  layoutSection,
}: ProjectViewControlsProps) {
  const { t } = useTranslation('projects')
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // A list with an inline quick-add row takes "C" and "+ Task" (design 25a);
  // anything else (board, calendar, timeline) opens the dialog.
  const controlsId = useId()
  const registerControls = useQuickAddStore((s) => s.registerControls)
  const ownsHotkey = useQuickAddStore((s) => s.controlIds.at(-1) === controlsId)
  useEffect(() => registerControls(controlsId), [registerControls, controlsId])
  const handleCreate = useCallback(() => {
    const openInline = useQuickAddStore.getState().openInline
    if (openInline) openInline()
    else setIsCreateOpen(true)
  }, [])

  useHotkeys('c', handleCreate, { preventDefault: true, enabled: ownsHotkey })

  return (
    // On phones the switcher drops to its own full-width row under the title
    // (design 36a); the shell's breadcrumb bar wraps to make room.
    <div className="flex items-center gap-1.5 phone:contents">
      <div className="phone:order-last phone:basis-full phone:pb-2">
        <ViewTypeSwitcher projectId={projectId} isSpace={scope === 'space'} />
      </div>
      <div className="mx-1 h-4 w-px bg-border phone:hidden" aria-hidden />
      <FilterTriggerButton projectId={projectId} hiddenFields={hiddenFields} pinnedFields={pinnedFields} scope={scope} />
      <LayoutButton
        scope={{ kind: scope === 'space' ? 'space' : 'project', entityId: projectId }}
        layoutSection={layoutSection}
      />
      <Button
        size="sm"
        className={cn(toolbarButtonClass, 'ml-0.5 phone:hidden')}
        onClick={handleCreate}
        aria-label={t('layout.newTask')}
        aria-keyshortcuts="c"
      >
        <Plus className="size-3.5" />
        {t('display.task')}
        <kbd className="ml-0.5 rounded bg-white/15 px-1 font-mono text-[10.5px] opacity-80">C</kbd>
      </Button>
      <QuickCreateTaskDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        defaultProjectId={projectId}
      />
    </div>
  )
}
